import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigationType } from 'react-router-dom';
import { Search, MoreVertical, Trash2, Download, Briefcase, Users, Layers } from 'lucide-react';
import { toast } from 'sonner';
import type { CandidateStage } from '../../types';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { MultiSelectFilter } from '../../components/filters/MultiSelectFilter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { getApiErrorMessage } from '../../libs/api/errorHandler';
import { listJobDescriptions } from '../../libs/api/jobDescriptionService';
import { deleteApplication } from '../../libs/api/pipelineService';
import { WarningModal } from '../../components/AppStatusModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { CANDIDATES_QUERY_KEY, fetchCandidatesFromPipeline } from './candidateData';
import {
  escapeCsvCell,
  filterCandidates,
  formatVnPhoneForCsv,
  formatVnPhoneForDisplay,
  isSortKey,
  sortCandidates,
  type CandidateListItem,
  type SortConfig,
  type SortKey,
} from './candidateListUtils';
import {
  formatInterviewRound,
  formatInterviewSchedule,
  getInterviewRoundBadgeClass,
} from './candidateInterviewUtils';
import { InterviewDayFilter } from './InterviewDayFilter';
import { SortableTableHeader } from './SortableTableHeader';
import { Checkbox } from '@/components/ui/checkbox';
import {
  applyScrollAfterPaint,
  candidateListScrollKey,
  parseCandidateListScrollSnapshot,
  type CandidateListScrollSnapshot,
} from '@/libs/navigation/routeScrollStorage';

interface CandidateListProps {
  onSelectCandidate: (applicationId: string) => void;
}

export { filterCandidates, formatVnPhoneForCsv, formatVnPhoneForDisplay };

const CandidateList: React.FC<CandidateListProps> = ({ onSelectCandidate }) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navType = useNavigationType();
  const listScrollRef = useRef<HTMLDivElement>(null);
  const topHorizontalScrollRef = useRef<HTMLDivElement>(null);
  const tableHorizontalScrollRef = useRef<HTMLDivElement | null>(null);
  const skipFilterPageResetRef = useRef(false);
  const popFiltersAppliedRef = useRef(false);
  const popScrollAppliedRef = useRef(false);

  const scrollStorageKey = candidateListScrollKey(location.pathname);

  const PAGE_SIZE = 20;
  const [search, setSearch] = useState('');
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [stageFilters, setStageFilters] = useState<string[]>([]);
  const [teamFilters, setTeamFilters] = useState<string[]>([]);
  const [interviewDayFilter, setInterviewDayFilter] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    direction: 'desc',
    key: 'appliedDate',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<CandidateListItem | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [tableScrollWidth, setTableScrollWidth] = useState(1420);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: CANDIDATES_QUERY_KEY,
    queryFn: () => fetchCandidatesFromPipeline(),
  });

  const { data: jobOptionsData } = useQuery({
    queryKey: ['candidate-role-options'],
    queryFn: () => listJobDescriptions({ page: 1, page_size: 100 }),
  });

  const deleteApplicationMutation = useMutation({
    mutationFn: (applicationId: string) => deleteApplication(applicationId),
  });

  useEffect(() => {
    if (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load candidate list'));
    }
  }, [error]);

  const candidates = useMemo<CandidateListItem[]>(
    () => (data as CandidateListItem[] | undefined) ?? [],
    [data],
  );

  const roleOptions = useMemo(() => {
    const options = new Map<string, string>();
    (jobOptionsData?.items || []).forEach((item) => {
      options.set(item.id, item.title);
    });

    candidates.forEach((item) => {
      if (item.jdId) {
        options.set(item.jdId, item.role);
      }
    });

    return Array.from(options.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [candidates, jobOptionsData?.items]);

  const teamOptions = useMemo(() => {
    const values = new Set<string>();
    candidates.forEach((item) => {
      if (item.department) {
        values.add(item.department);
      }
    });
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [candidates]);

  const jdThresholdMap = useMemo(() => {
    const thresholds = new Map<string, number>();

    (jobOptionsData?.items || []).forEach((item) => {
      if (typeof item.ai_threshold === 'number') {
        thresholds.set(item.id, item.ai_threshold);
      }
    });

    return thresholds;
  }, [jobOptionsData?.items]);

  const filteredCandidates = useMemo(
    () =>
      filterCandidates(candidates, {
        interviewDayFilter,
        search,
        roleFilters,
        stageFilters,
        teamFilters,
      }),
    [candidates, interviewDayFilter, roleFilters, search, stageFilters, teamFilters],
  );

  const sortedCandidates = useMemo(
    () => sortCandidates(filteredCandidates, sortConfig),
    [filteredCandidates, sortConfig],
  );

  const stages: string[] = ['New', 'Screening', 'Interview', 'Offer', 'Hired', 'Rejected'];
  const stageOptions = useMemo(() => stages.map((stage) => ({ value: stage, label: stage })), []);

  const isFilterActive =
    search.trim().length > 0 ||
    roleFilters.length > 0 ||
    stageFilters.length > 0 ||
    teamFilters.length > 0 ||
    interviewDayFilter.length > 0;

  const handleResetFilters = () => {
    setSearch('');
    setRoleFilters([]);
    setStageFilters([]);
    setTeamFilters([]);
    setInterviewDayFilter('');
  };

  const totalPages = Math.max(1, Math.ceil(sortedCandidates.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedCandidates = sortedCandidates.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [interviewDayFilter, search, roleFilters, sortConfig, stageFilters, teamFilters]);

  useEffect(() => {
    if (navType === 'PUSH' || navType === 'REPLACE') {
      sessionStorage.removeItem(scrollStorageKey);
    }
  }, [navType, scrollStorageKey]);

  useEffect(() => {
    if (navType !== 'POP') {
      popFiltersAppliedRef.current = false;
      popScrollAppliedRef.current = false;
      return;
    }
    if (isLoading) return;
    if (popFiltersAppliedRef.current) return;

    const snapshot = parseCandidateListScrollSnapshot(sessionStorage.getItem(scrollStorageKey));
    if (!snapshot) return;

    popFiltersAppliedRef.current = true;

    const snapshotRoleFilters = Array.isArray(snapshot.roleFilters) ? snapshot.roleFilters : [];
    const snapshotStageFilters = Array.isArray(snapshot.stageFilters) ? snapshot.stageFilters : [];
    const snapshotTeamFilters = Array.isArray(snapshot.teamFilters) ? snapshot.teamFilters : [];

    const filtered = filterCandidates(candidates, {
      interviewDayFilter: snapshot.interviewDayFilter ?? '',
      search: snapshot.search,
      roleFilters: snapshotRoleFilters,
      stageFilters: snapshotStageFilters,
      teamFilters: snapshotTeamFilters,
    });
    const tp = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const page = Math.min(Math.max(1, snapshot.currentPage), tp);

    skipFilterPageResetRef.current = true;
    setSearch(snapshot.search);
    setRoleFilters(snapshotRoleFilters);
    setStageFilters(snapshotStageFilters);
    setTeamFilters(snapshotTeamFilters);
    setInterviewDayFilter(snapshot.interviewDayFilter ?? '');
    if (isSortKey(snapshot.sortKey) && snapshot.sortDirection) {
      setSortConfig({
        direction: snapshot.sortDirection,
        key: snapshot.sortKey,
      });
    }
    setCurrentPage(page);
  }, [navType, scrollStorageKey, isLoading, candidates]);

  useEffect(() => {
    if (navType !== 'POP') return;
    if (isLoading) return;
    if (!popFiltersAppliedRef.current) return;
    if (popScrollAppliedRef.current) return;

    const snapshot = parseCandidateListScrollSnapshot(sessionStorage.getItem(scrollStorageKey));
    if (!snapshot) return;

    popScrollAppliedRef.current = true;
    applyScrollAfterPaint(() => {
      const el = listScrollRef.current;
      if (!el) return;
      el.scrollTop = snapshot.scrollTop;
      const tableScroller =
        tableHorizontalScrollRef.current ??
        el.querySelector<HTMLDivElement>('[data-slot="table-container"]');
      if (tableScroller) {
        tableScroller.scrollLeft = snapshot.scrollLeft;
      }
      if (topHorizontalScrollRef.current) {
        topHorizontalScrollRef.current.scrollLeft = snapshot.scrollLeft;
      }
    });
  }, [navType, isLoading, currentPage, totalPages, paginatedCandidates.length, scrollStorageKey]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  useEffect(() => {
    const listScroller = listScrollRef.current;
    const topScroller = topHorizontalScrollRef.current;
    const tableScroller = listScroller?.querySelector<HTMLDivElement>(
      '[data-slot="table-container"]',
    );

    if (!topScroller || !tableScroller) return undefined;

    tableHorizontalScrollRef.current = tableScroller;

    const updateScrollWidth = () => {
      setTableScrollWidth(tableScroller.scrollWidth);
      topScroller.scrollLeft = tableScroller.scrollLeft;
    };
    const syncFromTable = () => {
      if (topScroller.scrollLeft !== tableScroller.scrollLeft) {
        topScroller.scrollLeft = tableScroller.scrollLeft;
      }
    };
    const syncFromTop = () => {
      if (tableScroller.scrollLeft !== topScroller.scrollLeft) {
        tableScroller.scrollLeft = topScroller.scrollLeft;
      }
    };

    updateScrollWidth();
    tableScroller.addEventListener('scroll', syncFromTable, { passive: true });
    topScroller.addEventListener('scroll', syncFromTop, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollWidth);
    resizeObserver.observe(tableScroller);
    const table = tableScroller.querySelector('table');
    if (table) resizeObserver.observe(table);

    return () => {
      resizeObserver.disconnect();
      tableScroller.removeEventListener('scroll', syncFromTable);
      topScroller.removeEventListener('scroll', syncFromTop);
      if (tableHorizontalScrollRef.current === tableScroller) {
        tableHorizontalScrollRef.current = null;
      }
    };
  }, [isLoading, paginatedCandidates.length]);

  useEffect(() => {
    const valid = new Set(
      filteredCandidates.map((c) => c.applicationId).filter((id): id is string => Boolean(id)),
    );
    setSelectedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => {
        if (valid.has(id)) {
          next.add(id);
        }
      });
      if (next.size === prev.size) {
        for (const id of prev) {
          if (!next.has(id)) {
            return next;
          }
        }
        return prev;
      }
      return next;
    });
  }, [filteredCandidates]);

  const pageApplicationIds = useMemo(
    () => paginatedCandidates.map((c) => c.applicationId).filter((id): id is string => Boolean(id)),
    [paginatedCandidates],
  );

  const allPageSelected =
    pageApplicationIds.length > 0 && pageApplicationIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageApplicationIds.some((id) => selectedIds.has(id));

  const getStageStyles = (stage: CandidateStage) => {
    switch (stage) {
      case 'New':
        return 'bg-muted text-muted-foreground border-border';
      case 'Screening':
        return 'bg-info-muted text-info-muted-foreground border-info/30';
      case 'Interview':
        return 'bg-violet-50 text-violet-700 border-violet-100 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-800/60';
      case 'Offer':
        return 'bg-orange-50 text-orange-700 border-orange-100 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800/60';
      case 'Hired':
        return 'bg-success-muted text-success-muted-foreground border-success/30';
      case 'Rejected':
        return 'bg-error-muted text-error-muted-foreground border-error/30';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getScoreColor = (score?: number, threshold?: number) => {
    if (score === undefined || score === null) return 'text-text-subtle';
    if (typeof threshold === 'number') {
      return score >= threshold ? 'text-success' : 'text-destructive';
    }

    return 'text-text-main';
  };

  const handleSelectCandidate = (candidate: CandidateListItem) => {
    queryClient.setQueryData(['candidate-profile', candidate.id], candidate);

    if (!candidate.applicationId) {
      toast.error('Application ID is missing');
      return;
    }

    if (candidate.applicationId !== candidate.id) {
      queryClient.setQueryData(['candidate-profile', candidate.applicationId], candidate);
    }

    const el = listScrollRef.current;
    const snapshot: CandidateListScrollSnapshot = {
      v: 1,
      scrollTop: el?.scrollTop ?? 0,
      scrollLeft: tableHorizontalScrollRef.current?.scrollLeft ?? 0,
      currentPage,
      interviewDayFilter,
      search,
      roleFilters,
      sortDirection: sortConfig.direction,
      sortKey: sortConfig.key,
      stageFilters,
      teamFilters,
    };
    sessionStorage.setItem(scrollStorageKey, JSON.stringify(snapshot));

    onSelectCandidate(candidate.applicationId);
  };

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleDeleteApplication = (candidate: CandidateListItem) => {
    if (!candidate.applicationId) {
      toast.error('Application ID is missing');
      return;
    }

    setDeleteTarget(candidate);
    setIsDeleteModalOpen(true);
  };

  const toggleSelectPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        pageApplicationIds.forEach((id) => next.add(id));
      } else {
        pageApplicationIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const handleDownloadCsv = () => {
    const rows = candidates.filter((c) => c.applicationId && selectedIds.has(c.applicationId));
    if (rows.length === 0) return;

    const header = ['Tên ứng viên', 'Ngày sinh', 'Position', 'Department', 'SĐT', 'Email'];
    const lines = [
      header.map(escapeCsvCell).join(','),
      ...rows.map((c) =>
        [c.name, c.dateOfBirth, c.role, c.department ?? '', formatVnPhoneForCsv(c.phone), c.email]
          .map((v) => escapeCsvCell(v ?? ''))
          .join(','),
      ),
    ];
    const csv = lines.join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `candidates_${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${rows.length} candidate${rows.length === 1 ? '' : 's'} to CSV`);
  };

  const confirmDeleteApplication = async () => {
    const candidate = deleteTarget;
    const applicationId = candidate?.applicationId;

    if (!candidate || !applicationId) {
      toast.error('Application ID is missing');
      return;
    }

    try {
      await deleteApplicationMutation.mutateAsync(applicationId);
      queryClient.removeQueries({ queryKey: ['candidate-profile', candidate.id] });
      queryClient.removeQueries({ queryKey: ['candidate-profile', applicationId] });
      queryClient.invalidateQueries({ queryKey: CANDIDATES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
      setIsDeleteModalOpen(false);
      toast.success('Application deleted successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete application'));
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <WarningModal
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          setIsDeleteModalOpen(open);
          if (!open) {
            setDeleteTarget(null);
          }
        }}
        title="Delete application"
        description={
          deleteTarget
            ? `Are you sure you want to delete the application for ${deleteTarget.name}?`
            : 'Are you sure you want to delete this application?'
        }
        details="This action cannot be undone."
        confirmText={deleteApplicationMutation.isPending ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        onConfirm={() => {
          void confirmDeleteApplication();
        }}
      />

      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border-subtle bg-card shadow-sm">
        {/* Filters Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border-subtle bg-card p-4 xl:flex-nowrap">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 xl:flex-nowrap">
            <div className="relative min-w-[140px] max-w-xs flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle"
                size={16}
              />
              <Input
                type="text"
                placeholder="Search candidates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-bg-main pl-9"
              />
            </div>

            <MultiSelectFilter
              icon={<Users size={15} className="shrink-0 text-text-subtle" />}
              label="Team"
              hideLabel
              triggerClassName="w-[140px] min-w-0 shrink-0"
              allLabel="All Teams"
              searchPlaceholder="Search team..."
              emptyText="No team found."
              options={teamOptions}
              selected={teamFilters}
              onChange={setTeamFilters}
            />

            <MultiSelectFilter
              icon={<Briefcase size={15} className="shrink-0 text-text-subtle" />}
              label="Role"
              hideLabel
              triggerClassName="w-[140px] min-w-0 shrink-0"
              allLabel="All Roles"
              searchPlaceholder="Search role..."
              emptyText="No role found."
              options={roleOptions}
              selected={roleFilters}
              onChange={setRoleFilters}
            />

            <MultiSelectFilter
              icon={<Layers size={15} className="shrink-0 text-text-subtle" />}
              label="Stage"
              hideLabel
              triggerClassName="w-[140px] min-w-0 shrink-0"
              allLabel="All Stages"
              searchPlaceholder="Search stage..."
              emptyText="No stage found."
              options={stageOptions}
              selected={stageFilters}
              onChange={setStageFilters}
            />

            <InterviewDayFilter value={interviewDayFilter} onChange={setInterviewDayFilter} />

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              disabled={!isFilterActive}
              onClick={handleResetFilters}
            >
              Reset
            </Button>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="shrink-0"
              disabled={selectedIds.size === 0}
              onClick={() => handleDownloadCsv()}
              aria-label="Download CSV"
              title="Download CSV"
            >
              <Download className="size-4 2xl:mr-2" />
              <span className="hidden 2xl:inline">Download CSV</span>
            </Button>
            {isFetching && !isLoading ? (
              <span className="hidden text-xs font-medium text-text-subtle 2xl:inline">
                Updating...
              </span>
            ) : null}
          </div>
        </div>

        {/* Table Content */}
        <div
          ref={listScrollRef}
          className="min-h-0 flex-1 overflow-auto border-none bg-card py-0 text-card-foreground shadow-sm [&_[data-slot=table-container]]:overflow-x-hidden"
        >
          {isLoading ? (
            <div className="p-6 text-sm text-text-subtle">Loading candidates...</div>
          ) : null}
          <Table className="min-w-[1420px] table-fixed">
            <TableHeader>
              <TableRow className="bg-bg-main sticky top-0 z-10 border-border-subtle">
                <TableHead className="w-12 px-4 text-center">
                  <div className="flex justify-center">
                    <Checkbox
                      aria-label="Select all on this page"
                      disabled={pageApplicationIds.length === 0}
                      checked={allPageSelected ? true : somePageSelected ? 'indeterminate' : false}
                      onCheckedChange={(value) => toggleSelectPage(value === true)}
                    />
                  </div>
                </TableHead>
                <SortableTableHeader
                  align="left"
                  className="w-64"
                  label="Candidate"
                  sortConfig={sortConfig}
                  sortKey="candidate"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  align="left"
                  className="w-52"
                  label="Position"
                  sortConfig={sortConfig}
                  sortKey="position"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  className="w-32"
                  label="Source"
                  sortConfig={sortConfig}
                  sortKey="source"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  className="w-32"
                  label="Channel"
                  sortConfig={sortConfig}
                  sortKey="channel"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  align="left"
                  className="w-44"
                  label="Interview Schedule"
                  sortConfig={sortConfig}
                  sortKey="interviewSchedule"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  className="w-40"
                  label="Interview Result"
                  sortConfig={sortConfig}
                  sortKey="interviewResult"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  className="w-36"
                  label="Interview Round"
                  sortConfig={sortConfig}
                  sortKey="interviewRound"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  className="w-32"
                  label="Stage"
                  sortConfig={sortConfig}
                  sortKey="stage"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  className="w-28"
                  label="AI Score"
                  sortConfig={sortConfig}
                  sortKey="score"
                  onSort={handleSort}
                />
                <SortableTableHeader
                  align="left"
                  className="w-40"
                  label="Applied Date"
                  sortConfig={sortConfig}
                  sortKey="appliedDate"
                  onSort={handleSort}
                />
                <TableHead className="w-24 px-6 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedCandidates.map((candidate) => (
                <TableRow
                  key={candidate.applicationId}
                  onClick={() => handleSelectCandidate(candidate)}
                  className="table-row-hover cursor-pointer group border-b border-border-subtle"
                >
                  <TableCell
                    className="w-12 px-4 py-4"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="flex justify-center">
                      <Checkbox
                        aria-label={`Select ${candidate.name}`}
                        disabled={!candidate.applicationId}
                        checked={Boolean(
                          candidate.applicationId && selectedIds.has(candidate.applicationId),
                        )}
                        onCheckedChange={(value) => {
                          const id = candidate.applicationId;
                          if (!id) return;
                          const on = value === true;
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (on) {
                              next.add(id);
                            } else {
                              next.delete(id);
                            }
                            return next;
                          });
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 shrink-0 rounded-xl bg-primary-light flex items-center justify-center text-primary font-bold text-base border border-primary/10 shadow-sm">
                        {candidate.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-base font-bold text-text-main group-hover:text-primary transition-colors">
                          {candidate.name}
                        </div>
                        <div className="text-sm text-text-subtle">
                          {candidate.phone ? formatVnPhoneForDisplay(candidate.phone) : '--'}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="w-52 max-w-52 px-6 py-4 text-left">
                    <div
                      className="line-clamp-2 max-w-40 break-words text-base font-medium leading-5 text-text-main"
                      title={candidate.role}
                    >
                      {candidate.role}
                    </div>
                    <div className="text-xs font-bold text-text-subtle uppercase tracking-wider">
                      Full-time
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-center text-sm font-medium text-text-main">
                    {candidate.source || '--'}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-center text-sm font-medium text-text-main">
                    {candidate.channel || '--'}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-left">
                    {(() => {
                      const schedule = formatInterviewSchedule(
                        candidate.nextInterviewAt,
                        candidate.nextInterviewDurationMinutes,
                      );
                      return schedule ? (
                        <div>
                          <div className="text-sm font-semibold text-text-main">
                            {schedule.date}
                          </div>
                          <div className="text-xs text-text-subtle">{schedule.timeRange}</div>
                        </div>
                      ) : (
                        <span className="font-semibold">N/A</span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-center">
                    <Badge
                      variant="outline"
                      className={
                        candidate.nextInterviewResult === 'PASSED'
                          ? 'border-success/30 bg-success-muted text-success-muted-foreground'
                          : candidate.nextInterviewResult === 'FAILED'
                            ? 'border-error/30 bg-error-muted text-error-muted-foreground'
                            : 'border-border-subtle bg-bg-main text-text-subtle'
                      }
                    >
                      {candidate.nextInterviewResult ?? 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-center">
                    {candidate.nextInterviewAt ? (
                      <Badge
                        className={`h-6 min-w-12 justify-center border px-2 text-xs font-bold ${getInterviewRoundBadgeClass(
                          candidate.nextInterviewResult,
                        )}`}
                      >
                        {formatInterviewRound(candidate.nextInterviewRound)}
                      </Badge>
                    ) : (
                      <span className="font-semibold text-text-subtle">N/A</span>
                    )}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-center">
                    <Badge
                      className={`text-xs font-bold uppercase tracking-wider ${getStageStyles(candidate.stage)}`}
                    >
                      {candidate.stage}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-center">
                    <div
                      className={`text-xl font-bold ${getScoreColor(
                        candidate.score,
                        jdThresholdMap.get(candidate.jdId) ?? candidate.jdThreshold,
                      )}`}
                    >
                      {candidate.score || '--'}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-left">
                    <div className="text-base font-medium text-text-main">
                      {candidate.appliedDate}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeleteApplication(candidate);
                          }}
                          className="text-destructive focus:text-destructive cursor-pointer"
                        >
                          <Trash2 size={14} /> Delete Application
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredCandidates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-text-subtle">
              <Search size={48} className="mb-4 opacity-10" />
              <div className="text-lg font-semibold">No candidates found</div>
              <p className="text-base">Try adjusting your search or filters.</p>
            </div>
          )}
        </div>

        <div
          ref={topHorizontalScrollRef}
          className="h-3 min-h-3 shrink-0 overflow-x-auto overflow-y-hidden border-t border-border-subtle bg-bg-main/30"
          aria-label="Horizontal candidate table scrollbar"
        >
          <div style={{ width: tableScrollWidth, height: 1 }} />
        </div>

        {/* Footer / Pagination Placeholder */}
        <div className="flex shrink-0 items-center justify-between border-t border-border-subtle bg-bg-main/30 px-6 py-4">
          <div className="text-sm text-text-subtle font-medium">
            Showing{' '}
            <span className="text-text-main">
              {filteredCandidates.length === 0 ? 0 : pageStart + 1}
            </span>{' '}
            -{' '}
            <span className="text-text-main">
              {Math.min(pageStart + PAGE_SIZE, filteredCandidates.length)}
            </span>{' '}
            of <span className="text-text-main">{candidates.length}</span> candidates
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1 || filteredCandidates.length === 0}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </Button>
            <div className="text-sm text-text-subtle font-semibold px-1">
              {currentPage}/{totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages || filteredCandidates.length === 0}
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateList;
